import React from 'react';
import {Tag} from 'antd';
import {useTranslation} from 'react-i18next';
import type {TFunction} from 'i18next';
import {PODReviewStatus} from '../../../../enums/DeliveryEnum';
import {getTranslatedPodReviewMessage} from '../../../../utils/podReviewMessageUtils';
import {PodAbnormalOrder, PodAbnormalTaskOrder} from '../graphql/types/PodAbnormalType';

/** POD 多语言文案的语言键 */
export type PodLang = 'zh' | 'en' | 'es';

/** 已知错误类型（与 podReviewMessageUtils / 移动端 PodReviewCard 对齐） */
export const KNOWN_ERROR_TYPES = ['NO_LABEL_PHOTO', 'WRONG_TRK', 'NO_HOUSE_APT', 'WRONG_HOUSE_APT'];

/** 临近截止阈值：剩余 < 1h（排序与飘红共用，单一口径，与移动端 DriverViolationReviewPage 一致） */
export const URGENT_THRESHOLD_MS = 60 * 60_000;

/** 审核状态 → antd Tag 颜色 */
const POD_STATUS_COLOR: Record<number, string> = {
    [PODReviewStatus.NOT_REVIEWED]: 'default',
    [PODReviewStatus.MACHINE_APPROVED]: 'green',
    [PODReviewStatus.MACHINE_DISAPPROVED]: 'red',
    [PODReviewStatus.MANUAL_PENDING]: 'orange',
    [PODReviewStatus.MANUAL_APPROVED]: 'green',
    [PODReviewStatus.MANUAL_DISAPPROVED]: 'red',
    [PODReviewStatus.MANUAL_CORRECTED]: 'green',
};

/** 审核状态 → i18n key（复用 desktop-web 既有 enum.podReviewStatus.*） */
const POD_STATUS_I18N: Record<number, string> = {
    [PODReviewStatus.NOT_REVIEWED]: 'enum.podReviewStatus.NOT_REVIEWED',
    [PODReviewStatus.MACHINE_APPROVED]: 'enum.podReviewStatus.MACHINE_APPROVED',
    [PODReviewStatus.MACHINE_DISAPPROVED]: 'enum.podReviewStatus.MACHINE_DISAPPROVED',
    [PODReviewStatus.MANUAL_PENDING]: 'enum.podReviewStatus.MANUAL_PENDING',
    [PODReviewStatus.MANUAL_APPROVED]: 'enum.podReviewStatus.MANUAL_APPROVED',
    [PODReviewStatus.MANUAL_DISAPPROVED]: 'enum.podReviewStatus.MANUAL_DISAPPROVED',
    [PODReviewStatus.MANUAL_CORRECTED]: 'enum.podReviewStatus.MANUAL_CORRECTED',
};

/** 审核状态 Tag，列表与详情抽屉共用 */
export const PodStatusTag: React.FC<{status: number}> = ({status}) => {
    const {t} = useTranslation();
    return (
        <Tag color={POD_STATUS_COLOR[status] ?? 'default'}>
            {t(POD_STATUS_I18N[status] ?? 'enum.podReviewStatus.UNKNOWN')}
        </Tag>
    );
};

/** i18n.language → POD 多语言文案的语言键 */
export const getPodLang = (language?: string): PodLang => {
    const l = language || 'en';
    if (l.startsWith('zh')) return 'zh';
    if (l.startsWith('es')) return 'es';
    return 'en';
};

/**
 * 剩余可修改毫秒数；无 podUpdateTimeString（epoch 秒）或非法时间串归一为 Infinity
 * （沉到未审核组末尾），避免 NaN 进入排序比较器破坏全序。
 * Date.now() 仅在调用期执行，不在模块加载期。
 */
export const getRemainingMs = (o: PodAbnormalTaskOrder): number => {
    const podUpdateTimeString = o.podUpdateTimeString;
    if (!podUpdateTimeString) return Infinity;
    const epochSeconds = Number(podUpdateTimeString);
    if (Number.isNaN(epochSeconds)) return Infinity;
    return epochSeconds * 1000 - Date.now();
};

/** 未审核且剩余 < 1h 视为临近截止（飘红 + 排序置顶共用同一判定） */
export const isPodUrgent = (o: PodAbnormalTaskOrder): boolean => {
    if (o.orderWithAddress.order.podReviewStatus === PODReviewStatus.MANUAL_APPROVED) return false;
    const remainingMs = getRemainingMs(o);
    return remainingMs > 0 && remainingMs < URGENT_THRESHOLD_MS;
};

/**
 * 驳回原因文本：podReviewReasons 优先（取 l2，否则 l1），否则回退 podReviewMessage。
 * 列表与详情抽屉共用，保证同一订单两处展示一致。
 */
export const resolvePodReasonText = (
    order: PodAbnormalOrder,
    lang: PodLang,
    t: TFunction,
): string => {
    const reasons = order.podReviewReasons;
    if (reasons && reasons.length > 0) {
        const texts = reasons.flatMap(l1 =>
            l1.l2Reasons.length > 0 ? l1.l2Reasons.map(l2 => l2.l2Label[lang]) : [l1.l1Label[lang]],
        );
        return texts.length > 0 ? texts.join(', ') : '-';
    }
    const translated = getTranslatedPodReviewMessage(order.podReviewMessage) || [];
    const text = translated
        .map(msg =>
            KNOWN_ERROR_TYPES.includes(msg)
                ? (t(`podReview.reasons.${msg}`, {defaultValue: msg}) as string)
                : msg,
        )
        .filter(Boolean)
        .join(', ');
    return text || '-';
};
